import { Download, FileText, ImageIcon } from "lucide-react";
import React, { useRef } from "react";

interface InwardAttachmentUploadProps {
  accept: string;
  maxSizeMB: number;
  files: File[];
  multiple?: boolean;
  onChange: (files: File[]) => void;
  error?: string;
}

const getIcon = (type: string) => {
  if (type.includes("pdf")) return <FileText size={16} />;
  if (type.includes("image")) return <ImageIcon size={16} />;
  return <FileText size={16} />;
};

export const InwardAttachmentUpload: React.FC<InwardAttachmentUploadProps> = ({
  accept,
  maxSizeMB,
  files,
  multiple = false,
  onChange,
  error,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const acceptedTypes = accept.split(",");
    const validFiles: File[] = [];

    Array.from(selectedFiles).forEach((file) => {
      if (!acceptedTypes.includes(file.type)) return;
      if (file.size > maxSizeMB * 1024 * 1024) return;
      validFiles.push(file);
    });

    if (!validFiles.length) return;

    onChange(multiple ? [...files, ...validFiles] : [validFiles[0]]);
  };

  const removeFile = (index: number) => {
    const updated = [...files];
    updated.splice(index, 1);
    onChange(updated);
  };

  return (
    <div>
      <div
        id="warehouse-inward-attachment-upload-dropzone"
        className="
          mt-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer bg-white
          hover:border-gray-300 transition-colors
        "
        onClick={() => inputRef.current?.click()}
      >
        <p className="text-sm text-gray-500">
          Click to upload or drag and drop
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {accept} up to {maxSizeMB}MB
        </p>
      </div>

      <input
        id="warehouse-inward-attachment-upload-input"
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={(e) => handleFiles(e.target.files)}
      />

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-2 space-y-2">
          {files.map((file, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between text-sm bg-white border rounded-md px-3 py-2"
            >
              <div className="flex items-start md:items-center gap-3 flex-1 min-w-0">
                <div className="flex items-start md:items-center gap-2 flex-1 min-w-0">
                  <div className="text-gray-400 shrink-0">
                    {getIcon(file.type)}
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:gap-2 min-w-0">
                    <span className="text-gray-700 truncate font-medium text-sm md:text-base">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-400 md:hidden">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                </div>

                <div className="hidden md:block text-xs text-gray-400 shrink-0">
                  ({(file.size / 1024).toFixed(1)} KB)
                </div>
              </div>

              <div className="flex items-center gap-4 ml-4 shrink-0">
                <a
                  href={URL.createObjectURL(file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs font-semibold"
                >
                  <Download size={14} />
                  View
                </a>
                <button
                  id={`warehouse-inward-attachment-upload-remove-${idx}`}
                  type="button"
                  onClick={() => removeFile(idx)}
                  className="text-red-500 hover:text-red-700 text-xs font-semibold"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};
